import { useCallback } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import emitUserInteractionEvent, {
  UserInteractionName,
} from "./emitUserInteractionEvent";
import { SERVICE_NAME } from "@zapier/utils";

export enum TemplateKebabInteractionOption {
  ChangeOwner = "change_owner",
  Delete = "delete",
  Duplicate = "duplicate",
  Edit = "edit",
  Share = "share",
  Use = "use",
  ViewDetails = "view_details",
}

/**
 * Guided template row kebab interactions.
 *
 * The `Edit` option is the asset-management half of the configurable-templates
 * edit funnel (CRTN-10160); the editor emits the rest of that funnel as
 * `configurable_template.*` events. The two join on template_id + account_id —
 * they cannot share an interaction name because this repo's names come from the
 * closed UserInteractionName enum above.
 */
export function useEmitTemplateKebabInteractionEvent() {
  const accountId = useCurrentAccountId();

  const emit = useCallback(
    (
      optionSelected: TemplateKebabInteractionOption,
      customTrackingFields?: {
        template_id?: string;
        customuser_id?: number;
      },
    ) =>
      // Returned rather than discarded so call sites that emit immediately
      // before a navigation can attach a .catch and keep a rejected emit from
      // becoming an unhandled rejection.
      emitUserInteractionEvent({
        accountId,
        eventAction: "click",
        interactionName: UserInteractionName.TemplateKebab,
        optionSelected,
        systemName: SERVICE_NAME,
        customTrackingFields: {
          ...customTrackingFields,
          // Kept in step with getEditTemplateUrl, which puts the same value on
          // the URL the editor reads its referring_source from.
          referring_source: "guided_templates_assets",
        },
      }),
    [accountId],
  );

  return {
    emit,
  };
}
